import { useQuery } from 'react-query';
import { baseApiUrl, fetcher, getToken, ServerErrorResponse } from 'data_sources/constants';
import { PaginatedApiCollectionResponse } from 'types/api_response';
import _ from 'lodash';

export interface TrainingCourseThumbnail {
  default: string;
  medium: string;
  small: string;
}

export interface TrainingCourse {
  id: string;
  name: string;
  slug: string;
  description: string;
  creditMinutes: number;
  thumbnail: TrainingCourseThumbnail;
}

export interface TrainingCourseApiItem {
  course: TrainingCourse;
}

export type TrainingCoursesResponse = PaginatedApiCollectionResponse<TrainingCourseApiItem>;

export const getTrainingCourses = async (
  orgId: string,
  params: Record<string, any> = {}
): Promise<PaginatedApiCollectionResponse<TrainingCourse>> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${orgId}/training/courses`;

  const paramString = new URLSearchParams(
    Object.entries({
      ...params,
    })
      .filter(([_k, v]) => !_.isEmpty(v))
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
  ).toString();

  const response = await fetcher({
    method: 'get',
    url: `${url}?${paramString}`,
  }).catch((err) => {
    console.warn('error retrieving training courses', err);
    throw err;
  });

  // Handle case where API returns array directly or wrapped response
  if (Array.isArray(response)) {
    return { content: response };
  }

  // Transform the API response to extract courses from the nested structure
  const apiResponse = response as TrainingCoursesResponse;
  return {
    count: apiResponse.count,
    nextToken: apiResponse.nextToken,
    content: apiResponse.content.map((item) => item.course),
  };
};

export default function useTrainingCourses(
  orgId: string,
  params: Record<string, any> = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const idToken = getToken();
  return useQuery<PaginatedApiCollectionResponse<TrainingCourse>, Error>(
    ['training_courses', orgId, params, idToken],
    async () => getTrainingCourses(orgId, params),
    {
      ...options,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      enabled: !!orgId,
    }
  );
}
